/** Optional "vehicles in the yard" on an attempt: make → model, color, tag (+ state), photo. */
import { useRef, useState } from 'react';
import { formatVehicle, US_STATES, VEHICLE_COLORS, VEHICLE_MAKES, VEHICLE_MODELS } from '@civilos/domain';

export interface VehicleDraft {
  make: string;
  model: string;
  color: string;
  plate: string;
  plateState: string;
  photo: { dataBase64: string; preview: string } | null;
}

const OTHER = '__other';
const blank = (): VehicleDraft => ({ make: '', model: '', color: '', plate: '', plateState: 'SC', photo: null });

/** What goes in the attempt payload (works offline: the photo travels with the queued attempt). */
export const vehiclesPayload = (list: VehicleDraft[]) =>
  list
    .filter((v) => v.make || v.model || v.color || v.plate)
    .map((v) => ({
      make: v.make || null,
      model: v.model || null,
      color: v.color || null,
      plate: v.plate.trim() || null,
      plateState: v.plate.trim() ? v.plateState : null,
      photo: v.photo ? { dataBase64: v.photo.dataBase64 } : null,
    }));

/** Shrink a camera photo to ≤1280 px JPEG so it syncs quickly over a weak signal. */
async function compress(file: File): Promise<{ dataBase64: string; preview: string }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, bad) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => bad(new Error('Could not read that photo'));
      i.src = url;
    });
    const scale = Math.min(1, 1280 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(img.naturalWidth * scale);
    c.height = Math.round(img.naturalHeight * scale);
    c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
    const preview = c.toDataURL('image/jpeg', 0.72);
    return { dataBase64: preview.split(',')[1]!, preview };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function VehiclesInYard({ value, onChange }: { value: VehicleDraft[]; onChange: (v: VehicleDraft[]) => void }) {
  const set = (i: number, patch: Partial<VehicleDraft>) => onChange(value.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  return (
    <div className="veh">
      {value.map((v, i) => (
        <VehicleRow key={i} n={i + 1} v={v} set={(p) => set(i, p)} remove={() => onChange(value.filter((_, j) => j !== i))} />
      ))}
      {value.length < 6 && (
        <button type="button" className="btn veh-add" onClick={() => onChange([...value, blank()])}>
          + Add vehicle in the yard <span className="muted small">(optional)</span>
        </button>
      )}
    </div>
  );
}

function VehicleRow({ n, v, set, remove }: { n: number; v: VehicleDraft; set: (p: Partial<VehicleDraft>) => void; remove: () => void }) {
  const [otherMake, setOtherMake] = useState(!!v.make && !VEHICLE_MAKES.includes(v.make));
  const models = VEHICLE_MODELS[v.make] ?? [];
  const [otherModel, setOtherModel] = useState(!!v.model && !models.includes(v.model));
  const [err, setErr] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  return (
    <fieldset className="veh-row">
      <legend>
        Vehicle {n}
        {(v.make || v.color || v.plate) && <span className="muted small"> — {formatVehicle({ ...v, plate: v.plate || null })}</span>}
      </legend>
      <div className="veh-grid">
        <label>
          Make
          {otherMake ? (
            <input value={v.make} onChange={(e) => set({ make: e.target.value })} placeholder="Make" autoFocus />
          ) : (
            <select
              aria-label={`Vehicle ${n} make`}
              value={v.make}
              onChange={(e) => {
                if (e.target.value === OTHER) {
                  setOtherMake(true);
                  set({ make: '', model: '' });
                } else set({ make: e.target.value, model: '' });
                setOtherModel(false);
              }}
            >
              <option value="">Make…</option>
              {VEHICLE_MAKES.map((m) => (
                <option key={m}>{m}</option>
              ))}
              <option value={OTHER}>Other…</option>
            </select>
          )}
        </label>
        <label>
          Model
          {otherModel || otherMake ? (
            <input value={v.model} onChange={(e) => set({ model: e.target.value })} placeholder="Model" />
          ) : (
            <select
              aria-label={`Vehicle ${n} model`}
              value={v.model}
              disabled={!v.make}
              onChange={(e) => {
                if (e.target.value === OTHER) {
                  setOtherModel(true);
                  set({ model: '' });
                } else set({ model: e.target.value });
              }}
            >
              <option value="">{v.make ? 'Model…' : 'Pick make first'}</option>
              {models.map((m) => (
                <option key={m}>{m}</option>
              ))}
              <option value={OTHER}>Other…</option>
            </select>
          )}
        </label>
        <label>
          Color
          <select aria-label={`Vehicle ${n} color`} value={v.color} onChange={(e) => set({ color: e.target.value })}>
            <option value="">Color…</option>
            {VEHICLE_COLORS.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <label>
          Tag
          <span className="veh-tag">
            <select aria-label={`Vehicle ${n} tag state`} value={v.plateState} onChange={(e) => set({ plateState: e.target.value })}>
              {US_STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <input aria-label={`Vehicle ${n} tag`} value={v.plate} onChange={(e) => set({ plate: e.target.value.toUpperCase().replace(/[^A-Z0-9 -]/g, '').slice(0, 12) })} placeholder="ABC123" autoCapitalize="characters" />
          </span>
        </label>
      </div>
      <div className="veh-photo">
        {v.photo ? (
          <>
            <img src={v.photo.preview} alt={`Vehicle ${n}`} />
            <button type="button" className="btn sm ghost" onClick={() => set({ photo: null })}>
              Remove photo
            </button>
          </>
        ) : (
          <button type="button" className="btn sm" onClick={() => file.current?.click()}>
            Add photo (optional)
          </button>
        )}
        <input
          ref={file}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            try {
              setErr(null);
              set({ photo: await compress(f) });
            } catch (x) {
              setErr((x as Error).message);
            }
          }}
        />
        <span className="grow" />
        <button type="button" className="btn sm ghost" onClick={remove}>
          Remove vehicle
        </button>
      </div>
      {err && <p className="small bad">{err}</p>}
    </fieldset>
  );
}
